/**
 * Счёт сигнала: сколько доводов сошлось в одной точке.
 *
 * Семь блоков с разными весами, сумма приводится к ста. Блок, по
 * которому данных нет, выпадает из счёта вместе со своим весом — и счёт
 * снова собирается из оставшихся. Иначе отсутствие открытого интереса
 * молча срезало бы четверть у каждого сигнала, и порог перестал бы
 * что-либо значить.
 *
 * Поверх суммы три надбавки — доминирование USDT, премия и ставка
 * финансирования. Именно надбавки, а не восьмой блок: блок внутри суммы
 * разбавил бы знаменатель, и при нейтральном рынке все прежние счета
 * просели бы, хотя ничего не произошло.
 *
 * Вне зоны счёт гасится целиком. Подтверждения без места, к которому они
 * относятся, ничего не подтверждают: большой объём бывает где угодно.
 *
 * Перенесено из пайна «Выгодные зоны PRO». Пороги и веса блоков зашиты:
 * это состав индикатора. Снаружи — только порог сигнала и три галочки с
 * весами надбавок.
 *
 * Считается вся история, а не только текущий бар: сигнал, которого не
 * видно на прошлом, невозможно проверить. Чтобы это не стоило кадра, все
 * ленты заранее раскладываются по барам одним проходом, а скользящие
 * сигмы считаются через суммы — иначе поиск значения открытого интереса
 * на каждом из двух тысяч баров дал бы миллионы операций на каждый тик.
 *
 * Старшие таймфреймы берутся только закрытыми. Незакрытый четырёхчасовой
 * бар знает свою цену лишь через четыре часа, и метка, поставленная по
 * нему, выглядела бы на истории гениальным входом, которого никто не мог
 * совершить.
 */
/* ------------------------------------------------- зашитые настройки */
/** Окно, за которое меряется изменение открытого интереса. */
const OI_LENGTH = 6;
/** Окно нормировки: во сколько собственных сигм укладывается изменение. */
const OI_NORM = 100;
const OI_SIGMA = 1;
/** Во сколько раз объём должен превысить средний ради полного балла. */
const VOLUME_MULT = 1.3;
/** Какой долей размаха должна быть тень ради полного балла. */
const WICK_SHARE = 0.5;
/** Окно изменения доминирования USDT. */
const USDTD_LENGTH = 6;
const USDTD_NORM = 100;
/** Ниже этого перекос доминирования считается шумом. */
const USDTD_SIGMA = 0.5;
/** Нормировка и пороги перекоса премии и ставки. */
const SKEW_NORM = 100;
const SKEW_SIGMA = 1;
const SKEW_EXTREME = 2;
/** Средний объём меряется по этому окну. */
const VOLUME_WINDOW = 20;
/** Сколько свечей молчать после сигнала. */
const COOLDOWN = 10;
/**
 * Веса блоков. Порядок обязан совпадать с `BLOCK_NAMES`: подсказка,
 * назвавшая один блок и показавшая вес другого, хуже отсутствия
 * подсказки.
 */
const WEIGHTS = [20, 25, 15, 15, 10, 8, 7];
export const BLOCK_NAMES = [
    'зона',
    'открытый интерес',
    'режим OI',
    'реакция свечи',
    'объём',
    'ликвидность',
    'схождение таймфреймов',
];
/* ----------------------------------------------------------- расчёт */
/**
 * Счёт по блокам.
 *
 * `null` в блоке означает «данных нет», и такой блок уходит из
 * знаменателя целиком. Ноль вместо него был бы утверждением «блок
 * проверен и не сработал» — совсем другим по смыслу.
 */
export function buildScore(parts) {
    let earned = 0;
    let possible = 0;
    for (let i = 0; i < parts.length; i += 1) {
        const part = parts[i];
        if (part === null || part === undefined)
            continue;
        earned += Math.min(Math.max(part, 0), 1) * WEIGHTS[i];
        possible += WEIGHTS[i];
    }
    return possible > 0 ? (earned / possible) * 100 : 0;
}
/** Стандартное отклонение по последнему окну. */
function stdev(values, period) {
    const window = [];
    for (let i = values.length - 1; i >= 0 && window.length < period; i -= 1) {
        const value = values[i];
        if (value !== null && value !== undefined && Number.isFinite(value))
            window.push(value);
    }
    if (window.length < 2)
        return null;
    const mean = window.reduce((sum, value) => sum + value, 0) / window.length;
    const variance = window.reduce((sum, value) => sum + (value - mean) ** 2, 0) / (window.length - 1);
    return Math.sqrt(variance);
}
/** Значение ряда на момент бара: последняя точка не позже него. */
function valueAt(points, time) {
    let found = null;
    for (const point of points) {
        if (point.at > time)
            break;
        found = point.value;
    }
    return found;
}
/**
 * Перекос ленты в собственных сигмах.
 *
 * Порог в абсолютных числах пришлось бы подбирать заново под каждый
 * символ: ставка в 0.01% на одном рынке норма, на другом небывалое.
 * Вопрос не «сколько это», а «насколько это необычно здесь».
 */
function skew(points) {
    if (points.length === 0)
        return { side: 0, part: 0 };
    const values = points.slice(-SKEW_NORM).map((point) => point.value);
    const last = values[values.length - 1];
    const noise = stdev(values, SKEW_NORM);
    if (last === undefined || noise === null || noise <= 0)
        return { side: 0, part: 0 };
    const strength = last / noise;
    if (Math.abs(strength) < SKEW_SIGMA)
        return { side: 0, part: 0 };
    /*
     * Сторона берётся против знака перекоса: платят лонги — значит в
     * лонге тесно, и вытряхивать будут именно их.
     */
    return {
        side: strength > 0 ? -1 : 1,
        part: Math.min(1, Math.abs(strength) / Math.max(SKEW_EXTREME, 0.1)),
    };
}
/** Код зоны по рангу цены: 3…1 под лонг, −1…−3 под шорт, 0 — вне. */
function zoneCode(rank) {
    if (rank === null)
        return 0;
    if (rank <= 10)
        return 3;
    if (rank <= 20)
        return 2;
    if (rank <= 32)
        return 1;
    if (rank >= 90)
        return -3;
    if (rank >= 80)
        return -2;
    if (rank >= 68)
        return -1;
    return 0;
}
/**
 * Ряд, выровненный по барам графика.
 *
 * У открытого интереса, премии и ставки свой шаг: у одного он совпадает
 * со свечой, у другой — несколько часов. Для каждого бара берётся
 * последняя точка не позже него — одним проходом с указателем, а не
 * поиском по всему ряду на каждом баре: второе превращает расчёт по
 * истории в квадратичный и считается дольше кадра.
 */
export function alignTo(candles, points) {
    const out = new Array(candles.length).fill(null);
    let cursor = 0;
    let value = null;
    for (let i = 0; i < candles.length; i += 1) {
        const time = candles[i].time;
        while (cursor < points.length && points[cursor].at <= time) {
            value = points[cursor].value;
            cursor += 1;
        }
        out[i] = value;
    }
    return out;
}
/**
 * Скользящее стандартное отклонение.
 *
 * Через суммы значений и квадратов: окно в сотню баров на двух тысячах
 * баров прямым способом — двести тысяч операций на каждый из четырёх
 * рядов, и это на каждый пришедший тик.
 */
export function rollingStdev(values, period) {
    const out = new Array(values.length).fill(null);
    const window = [];
    let sum = 0;
    let squares = 0;
    for (let i = 0; i < values.length; i += 1) {
        const value = values[i];
        if (value !== null && value !== undefined && Number.isFinite(value)) {
            window.push(value);
            sum += value;
            squares += value * value;
            if (window.length > period) {
                const gone = window.shift();
                sum -= gone;
                squares -= gone * gone;
            }
        }
        if (window.length < 2)
            continue;
        const n = window.length;
        const variance = Math.max(0, (squares - (sum * sum) / n) / (n - 1));
        out[i] = Math.sqrt(variance);
    }
    return out;
}
/**
 * Перекос ленты в собственных сигмах, по каждому бару.
 *
 * Порог в абсолютных числах пришлось бы подбирать заново под каждый
 * символ: ставка в 0.01% на одном рынке норма, на другом небывалое.
 * Вопрос не «сколько это», а «насколько это необычно здесь».
 */
function skewSeries(values) {
    const noise = rollingStdev(values, SKEW_NORM);
    return values.map((value, i) => {
        const sigma = noise[i];
        if (value === null || value === undefined || sigma == null || sigma <= 0) {
            return { side: 0, part: 0 };
        }
        const strength = value / sigma;
        if (Math.abs(strength) < SKEW_SIGMA)
            return { side: 0, part: 0 };
        /*
         * Сторона берётся против знака перекоса: платят лонги — значит в
         * лонге тесно, и вытряхивать будут именно их.
         */
        return {
            side: (strength > 0 ? -1 : 1),
            part: Math.min(1, Math.abs(strength) / Math.max(SKEW_EXTREME, 0.1)),
        };
    });
}
/**
 * Ранги старших таймфреймов, разложенные по барам графика.
 *
 * Берётся тот бар старшего таймфрейма, который **успел закрыться** к
 * концу текущего. Взять незакрытый значило бы подглядеть в будущее:
 * четырёхчасовой бар знает свою цену только через четыре часа, а на
 * истории такая метка выглядела бы гениальным входом, которого никто не
 * мог совершить.
 */
export function alignRanks(candles, step, frames) {
    return frames.map((frame) => {
        const out = new Array(candles.length).fill(null);
        let cursor = 0;
        let value = null;
        for (let i = 0; i < candles.length; i += 1) {
            const edge = candles[i].time + step;
            while (cursor < frame.times.length && frame.times[cursor] + frame.step <= edge) {
                value = frame.ranks[cursor] ?? null;
                cursor += 1;
            }
            out[i] = value;
        }
        return out;
    });
}
/**
 * Счёт и отметки по всей истории.
 *
 * Считается за один проход: все ленты заранее разложены по барам, все
 * скользящие величины — через суммы. Иначе на каждом баре пришлось бы
 * заново искать значение открытого интереса и заново считать сигму, и
 * две тысячи баров превратились бы в миллионы операций на каждый тик.
 */
export function signalState(inputs, options) {
    const { candles, zones } = inputs;
    const n = candles.length;
    if (n < 2)
        return { buyScore: 0, sellScore: 0, regime: null, marks: [] };
    /*
     * Закрытая свеча — закон платформы, а не настройка вида. Сигнал по
     * набирающемуся бару живёт до его закрытия и исчезает: человек видел
     * то, чего в истории не окажется.
     */
    const lastIndex = options.closedOnly && inputs.livePartial ? n - 2 : n - 1;
    if (lastIndex < 1)
        return { buyScore: 0, sellScore: 0, regime: null, marks: [] };
    /* --- ленты, разложенные по барам ------------------------------------- */
    const oi = alignTo(candles, inputs.openInterest);
    const premium = skewSeries(alignTo(candles, inputs.premium));
    const funding = skewSeries(alignTo(candles, inputs.funding));
    const usdtd = alignTo(candles, inputs.usdtd.map((candle) => ({ at: candle.time, value: candle.close })));
    const usdtdChange = usdtd.map((value, i) => {
        const past = usdtd[i - USDTD_LENGTH];
        return value === null || past === null || past === undefined ? null : value - past;
    });
    const usdtdNoise = rollingStdev(usdtdChange, USDTD_NORM);
    /* --- открытый интерес: изменение и его необычность -------------------- */
    const oiChange = oi.map((value, i) => {
        const past = oi[i - OI_LENGTH];
        return value === null || past === null || past === undefined || past <= 0
            ? null
            : ((value - past) / past) * 100;
    });
    const oiNoise = rollingStdev(oiChange, OI_NORM);
    /* --- скользящие величины самих свечей --------------------------------- */
    const atrSeries = trueRangeAverage(candles);
    const volumeAverage = rollingMean(candles.map((candle) => candle.volume), VOLUME_WINDOW);
    /* --- ликвидность по барам --------------------------------------------- */
    const sweptSell = new Set();
    const sweptBuy = new Set();
    for (const level of inputs.levels) {
        if (level.to === null)
            continue;
        if (level.side === 'above')
            sweptSell.add(level.to);
        else
            sweptBuy.add(level.to);
    }
    /*
     * Карта на каждом баре, а не её сегодняшнее состояние.
     *
     * Уровень живёт от рождения до снятия или вытеснения. Оценивать июнь
     * по сентябрьской карте нельзя: к сентябрю всё июньское вытеснено
     * лимитом, и блок ликвидности молча получал ноль там, где тогда давал
     * полный балл. Ошибка занижала счёт тем сильнее, чем дальше в прошлое.
     *
     * Цены живых уровней держатся отсортированными: блоку нужно только
     * «есть ли что-то выше цены и что-то ниже», а это края множества.
     */
    const births = [...inputs.levels].sort((a, b) => a.from - b.from);
    const deaths = [...inputs.levels]
        .map((level) => ({
        price: level.price,
        at: Math.min(level.to ?? Infinity, level.evictedAt ?? Infinity),
    }))
        .filter((entry) => Number.isFinite(entry.at))
        .sort((a, b) => a.at - b.at);
    const alivePrices = [];
    let nextBirth = 0;
    let nextDeath = 0;
    /* --- проход по барам --------------------------------------------------- */
    const marks = [];
    let lastMark = -Infinity;
    let buyScore = 0;
    let sellScore = 0;
    let regimeText = null;
    const inside = (low, high, value) => low !== null && high !== null && value >= low && value <= high;
    for (let i = 1; i <= lastIndex; i += 1) {
        const candle = candles[i];
        // Состав карты на этот бар: родившиеся — внутрь, умершие — наружу.
        while (nextBirth < births.length && births[nextBirth].from <= candle.time) {
            insertPrice(alivePrices, births[nextBirth].price);
            nextBirth += 1;
        }
        while (nextDeath < deaths.length && deaths[nextDeath].at <= candle.time) {
            removePrice(alivePrices, deaths[nextDeath].price);
            nextDeath += 1;
        }
        const inBuy = inside(zones.buyOuterBottom[i] ?? null, zones.buyOuterTop[i] ?? null, candle.low);
        const inSell = inside(zones.sellOuterBottom[i] ?? null, zones.sellOuterTop[i] ?? null, candle.high);
        /*
         * Вне зоны счёт гасится целиком, и считать остальное незачем:
         * подтверждения без места, к которому они относятся, ничего не
         * подтверждают.
         */
        if (!inBuy && !inSell) {
            if (i === lastIndex) {
                buyScore = 0;
                sellScore = 0;
                regimeText = regimeOf(oiChange[i] ?? null, oiNoise[i] ?? null, candles, i, atrSeries[i] ?? 0).text;
            }
            continue;
        }
        const inBuyStrong = inside(zones.buyStrongBottom[i] ?? null, zones.buyStrongTop[i] ?? null, candle.low);
        const inBuyExtreme = inside(zones.buyExtremeBottom[i] ?? null, zones.buyExtremeTop[i] ?? null, candle.low);
        const inSellStrong = inside(zones.sellStrongBottom[i] ?? null, zones.sellStrongTop[i] ?? null, candle.high);
        const inSellExtreme = inside(zones.sellExtremeBottom[i] ?? null, zones.sellExtremeTop[i] ?? null, candle.high);
        const buyZonePart = inBuyExtreme ? 1 : inBuyStrong ? 0.75 : inBuy ? 0.5 : 0;
        const sellZonePart = inSellExtreme ? 1 : inSellStrong ? 0.75 : inSell ? 0.5 : 0;
        /* блоки 2 и 3: открытый интерес и его режим */
        const { regime, text, strength } = regimeOf(oiChange[i] ?? null, oiNoise[i] ?? null, candles, i, atrSeries[i] ?? 0);
        const oiPart = strength === null ? null : Math.min(1, Math.max(0, strength) / Math.max(OI_SIGMA, 0.1));
        const sellRegimePart = strength === null
            ? null
            : regime === 1
                ? 1
                : regime === 2
                    ? 0.6
                    : regime === 4
                        ? 0.3
                        : regime === 3
                            ? 0
                            : 0.25;
        const buyRegimePart = strength === null
            ? null
            : regime === 2
                ? 1
                : regime === 1
                    ? 0.6
                    : regime === 4
                        ? 0.5
                        : regime === 3
                            ? 0.2
                            : 0.25;
        /* блок 4: реакция свечи */
        const range = Math.max(candle.high - candle.low, Number.EPSILON);
        const upperWick = candle.high - Math.max(candle.open, candle.close);
        const lowerWick = Math.min(candle.open, candle.close) - candle.low;
        const sellOuterBottom = zones.sellOuterBottom[i] ?? null;
        const buyOuterTop = zones.buyOuterTop[i] ?? null;
        const sellReject = sellOuterBottom !== null && candle.high >= sellOuterBottom && candle.close < sellOuterBottom;
        const buyReject = buyOuterTop !== null && candle.low <= buyOuterTop && candle.close > buyOuterTop;
        const sellReactionPart = Math.min(1, upperWick / range / Math.max(WICK_SHARE, 0.05)) * 0.6 +
            (candle.close < candle.open ? 0.2 : 0) +
            (sellReject ? 0.2 : 0);
        const buyReactionPart = Math.min(1, lowerWick / range / Math.max(WICK_SHARE, 0.05)) * 0.6 +
            (candle.close > candle.open ? 0.2 : 0) +
            (buyReject ? 0.2 : 0);
        /* блок 5: объём */
        const average = volumeAverage[i];
        const volumePart = average == null || average <= 0
            ? null
            : Math.min(1, candle.volume / average / Math.max(VOLUME_MULT, 0.1));
        /* блок 6: ликвидность */
        const hasAbove = alivePrices.length > 0 && alivePrices[alivePrices.length - 1] > candle.close;
        const hasBelow = alivePrices.length > 0 && alivePrices[0] < candle.close;
        const sellLiquidityPart = sweptSell.has(candle.time) ? 1 : hasBelow ? 0.4 : 0;
        const buyLiquidityPart = sweptBuy.has(candle.time) ? 1 : hasAbove ? 0.4 : 0;
        /* блок 7: схождение таймфреймов */
        /*
         * Считаются только те таймфреймы, ранг которых известен.
         *
         * Раньше неизвестный ранг молча засчитывался как «не согласен», и
         * блок падал в ноль везде, где ряд старшего таймфрейма не дотянулся
         * до этого места истории. Счёт от этого систематически занижался на
         * прошлом — то есть ровно там, где по нему и проверяют.
         */
        let alignBuy = 0;
        let alignSell = 0;
        let known = 0;
        for (const ranks of inputs.rankSeries) {
            const rank = ranks[i] ?? null;
            if (rank === null)
                continue;
            known += 1;
            const code = zoneCode(rank);
            if (code > 0)
                alignBuy += 1;
            if (code < 0)
                alignSell += 1;
        }
        const buyMtfPart = known === 0 ? null : alignBuy / known;
        const sellMtfPart = known === 0 ? null : alignSell / known;
        /* сумма и надбавки */
        let buy = buildScore([
            buyZonePart,
            oiPart,
            buyRegimePart,
            buyReactionPart,
            volumePart,
            buyLiquidityPart,
            buyMtfPart,
        ]);
        let sell = buildScore([
            sellZonePart,
            oiPart,
            sellRegimePart,
            sellReactionPart,
            volumePart,
            sellLiquidityPart,
            sellMtfPart,
        ]);
        if (options.usdtd.on) {
            const change = usdtdChange[i];
            const noise = usdtdNoise[i];
            if (change != null && noise != null && noise > 0) {
                const strengthUsdtd = change / noise;
                if (Math.abs(strengthUsdtd) >= USDTD_SIGMA) {
                    // Рост доминирования означает выход в стейбл — сторону шорта.
                    if (strengthUsdtd > 0)
                        sell += options.usdtd.weight;
                    else
                        buy += options.usdtd.weight;
                }
            }
        }
        if (options.premium.on) {
            const { side, part } = premium[i];
            if (side > 0)
                buy += options.premium.weight * part;
            if (side < 0)
                sell += options.premium.weight * part;
        }
        if (options.funding.on) {
            const { side, part } = funding[i];
            if (side > 0)
                buy += options.funding.weight * part;
            if (side < 0)
                sell += options.funding.weight * part;
        }
        buy = inBuy ? Math.min(100, buy) : 0;
        sell = inSell ? Math.min(100, sell) : 0;
        if (i === lastIndex) {
            buyScore = buy;
            sellScore = sell;
            regimeText = text;
        }
        // Без открытого интереса сигнала нет вовсе, каким бы ни вышел счёт.
        const oiSatisfied = !options.requireOi || strength !== null;
        /*
         * С порогом сравнивается тот счёт, что написан над отметкой.
         *
         * Счёт дробный — вклады интереса, премии и финансирования дают
         * десятые, — а подпись целая. Раньше порог сравнивался с точным
         * числом: 84,6 подписывалось «85», но порог 85 его отсекал, и
         * отметка «85» исчезала ровно при пороге 85. Человек видит целое
         * число и настраивает целое — сравнивать надо его же.
         */
        const side = !oiSatisfied
            ? null
            : reachesThreshold(buy, options.threshold) && buy > sell
                ? 'buy'
                : reachesThreshold(sell, options.threshold) && sell >= buy
                    ? 'sell'
                    : null;
        /*
         * Пауза нужна не для порядка на графике: цена стоит в зоне десятками
         * свечей, и без неё один и тот же довод превращается в сплошную
         * полосу одинаковых отметок.
         */
        if (side && i - lastMark >= COOLDOWN) {
            lastMark = i;
            marks.push({
                time: candle.time,
                price: side === 'buy' ? candle.low : candle.high,
                side,
                score: side === 'buy' ? buy : sell,
            });
        }
    }
    return { buyScore, sellScore, regime: regimeText, marks };
}
/** Дотянул ли счёт до порога — по той же целой цифре, что подписана на графике. */
export function reachesThreshold(score, threshold) {
    return Math.round(score) >= threshold;
}
/**
 * Режим открытого интереса: что происходит внутри движения.
 *
 * Рост сам по себе стороны не имеет — на каждый новый лонг приходится
 * новый шорт. Смысл появляется только вместе с направлением цены за то
 * же окно. Вялое движение отдельного разбора не заслуживает: при почти
 * нулевом смещении знак чего угодно случаен.
 */
function regimeOf(change, noise, candles, i, atrValue) {
    const strength = change === null || noise === null || noise <= 0 ? null : change / noise;
    if (strength === null)
        return { regime: 0, text: null, strength: null };
    const past = candles[i - OI_LENGTH];
    const move = past ? candles[i].close - past.close : 0;
    const moved = Math.abs(move) >= atrValue * 0.25;
    const regime = !moved || Math.abs(strength) < OI_SIGMA ? 0 : move > 0 ? (strength > 0 ? 1 : 3) : strength > 0 ? 2 : 4;
    const text = regime === 1
        ? 'Набор лонгов'
        : regime === 2
            ? 'Набор шортов'
            : regime === 3
                ? 'Закрытие шортов'
                : regime === 4
                    ? 'Закрытие лонгов'
                    : 'Не выражено';
    return { regime, text, strength };
}
/** Место цены в отсортированном массиве. */
function boundOf(prices, price) {
    let low = 0;
    let high = prices.length;
    while (low < high) {
        const mid = (low + high) >>> 1;
        if (prices[mid] < price)
            low = mid + 1;
        else
            high = mid;
    }
    return low;
}
function insertPrice(prices, price) {
    prices.splice(boundOf(prices, price), 0, price);
}
function removePrice(prices, price) {
    const at = boundOf(prices, price);
    if (prices[at] === price)
        prices.splice(at, 1);
}
/** Скользящее среднее по окну. */
function rollingMean(values, period) {
    const out = new Array(values.length).fill(null);
    let sum = 0;
    for (let i = 0; i < values.length; i += 1) {
        sum += values[i];
        if (i >= period)
            sum -= values[i - period];
        if (i >= period - 1)
            out[i] = sum / period;
    }
    return out;
}
/** Средний истинный размах: масштаб «заметного» движения. */
function trueRangeAverage(candles) {
    const ranges = new Array(candles.length).fill(0);
    for (let i = 1; i < candles.length; i += 1) {
        const candle = candles[i];
        const previous = candles[i - 1];
        ranges[i] = Math.max(candle.high - candle.low, Math.abs(candle.high - previous.close), Math.abs(candle.low - previous.close));
    }
    return rollingMean(ranges, 14);
}
//# sourceMappingURL=zone-signals.js.map